import React, { useState } from 'react';
import vaultCrypt from '../utils/encryption';
import { computeMerkleRoot } from '../utils/merkle';
import IncrementalMerkleTree from '../utils/incremental-merkle';
import { credentialsAPI } from '../services/api';
// CRA/Webpack5 doesn't polyfill Node core modules; use the pre-bundled browser build.
import argon2 from 'argon2-browser/dist/argon2-bundled.min.js';

const BenchmarkRunner = ({ masterKey }) => {
  const [logs, setLogs] = useState([]);
  const [running, setRunning] = useState(false);
  const [includeArgon2, setIncludeArgon2] = useState(false);

  const log = (entry) => {
    setLogs((prev) => [...prev, { ...entry, ts: Date.now() }]);
  };

  const logEnv = () => {
    try {
      const nav = typeof navigator !== 'undefined' ? navigator : {};
      const win = typeof window !== 'undefined' ? window : {};
      log({
        op: 'env',
        userAgent: nav.userAgent || '',
        language: nav.language || '',
        hardwareConcurrency: nav.hardwareConcurrency || null,
        deviceMemory: nav.deviceMemory || null,
        screen: {
          width: win.innerWidth || null,
          height: win.innerHeight || null,
        },
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      });
    } catch {
      // Best-effort only
    }
  };

  const runPBKDF2 = async (iterations, isWarmup = false) => {
    if (!masterKey) {
      throw new Error('Master key required for PBKDF2 benchmark');
    }
    const salt = vaultCrypt.generateSalt();
    const t0 = performance.now();
    await vaultCrypt.deriveKey(masterKey, salt, iterations);
    const t1 = performance.now();
    log({
      op: isWarmup ? 'pbkdf2-warmup' : 'pbkdf2',
      timeMs: t1 - t0,
      iterations: iterations || vaultCrypt.iterations,
    });
  };

  const runArgon2id = async (config) => {
    if (!masterKey) {
      throw new Error('Master key required for Argon2id benchmark');
    }
    const saltBase64 = vaultCrypt.generateSalt();
    const binary = atob(saltBase64);
    const saltBytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      saltBytes[i] = binary.charCodeAt(i);
    }

    const { time = 2, mem = 64 * 1024, parallelism = 1 } = config || {};

    const t0 = performance.now();
    await argon2.hash({
      pass: masterKey,
      salt: saltBytes,
      time,
      mem,
      parallelism,
      hashLen: 32,
      type: argon2.ArgonType.Argon2id,
    });
    const t1 = performance.now();

    log({
      op: 'argon2id',
      timeMs: t1 - t0,
      params: { time, mem, parallelism },
    });
  };

  const runEncryptDecrypt = async (isWarmup = false) => {
    if (!masterKey) {
      throw new Error('Master key required for AES-GCM benchmark');
    }
    // Performance: Initialize session vault key for benchmark (simulates real login)
    // This ensures encryption uses pre-derived key (fast) instead of PBKDF2 per encrypt
    try {
      const userSalt = vaultCrypt.getOrGenerateUserSalt('benchmark@test.com');
      await vaultCrypt.initializeSessionVaultKey(masterKey, userSalt);
    } catch (e) {
      // If initialization fails, continue with legacy format
    }
    
    const password = vaultCrypt.generatePassword(16);
    // Encrypt
    const tEnc0 = performance.now();
    const enc = await vaultCrypt.encryptPassword(password, masterKey);
    const tEnc1 = performance.now();
    log({
      op: isWarmup ? 'encrypt-warmup' : 'encrypt',
      timeMs: tEnc1 - tEnc0,
    });
    // Decrypt
    const tDec0 = performance.now();
    const plain = await vaultCrypt.decryptPasswordAsync(
      enc.encryptedPassword,
      masterKey,
      enc.iv,
      enc.salt,
    );
    const tDec1 = performance.now();
    log({
      op: isWarmup ? 'decrypt-warmup' : 'decrypt',
      timeMs: tDec1 - tDec0,
    });
    if (plain !== password) {
      log({ op: 'decrypt-mismatch', expectedLen: password.length, gotLen: plain.length });
    }
  };

  const runMerkle = async (N, isWarmup = false) => {
    const tFetch0 = performance.now();
    const res = await credentialsAPI.listAll();
    const allCreds = res.data || res || [];
    const creds = allCreds.slice(0, N);
    const tFetch1 = performance.now();

    const tMerkle0 = performance.now();
    await computeMerkleRoot(creds);
    const tMerkle1 = performance.now();

    log({
      op: isWarmup ? 'merkle-warmup' : 'merkle',
      N,
      fetchMs: tFetch1 - tFetch0,
      merkleMs: tMerkle1 - tMerkle0,
      totalMs: (tFetch1 - tFetch0) + (tMerkle1 - tMerkle0),
    });
  };

  const runIncrementalMerkle = async (N, isWarmup = false) => {
    const tFetch0 = performance.now();
    const res = await credentialsAPI.listAll();
    const allCreds = res.data || res || [];
    const creds = allCreds.slice(0, N);
    const tFetch1 = performance.now();

    const tree = new IncrementalMerkleTree();
    const tInit0 = performance.now();
    await tree.initFromCredentials(creds);
    const tInit1 = performance.now();

    if (creds.length > 0) {
      const testCred = creds[0];
      const tUpdate0 = performance.now();
      await tree.updateLeaf(testCred._id || testCred.id, testCred);
      const tUpdate1 = performance.now();

      log({
        op: isWarmup ? 'merkle-incremental-warmup' : 'merkle-incremental',
        N,
        fetchMs: tFetch1 - tFetch0,
        initMs: tInit1 - tInit0,
        updateMs: tUpdate1 - tUpdate0,
        totalMs: (tFetch1 - tFetch0) + (tInit1 - tInit0) + (tUpdate1 - tUpdate0),
      });
    }
  };

  const runLoginE2E = async (N) => {
    if (!masterKey) {
      throw new Error('Master key required for login e2e benchmark');
    }

    const t0 = performance.now();

    const userSalt = vaultCrypt.getOrGenerateUserSalt('benchmark-login@test.com');
    await vaultCrypt.initializeSessionVaultKey(masterKey, userSalt);

    const tFetch0 = performance.now();
    const res = await credentialsAPI.listAll();
    const allCreds = res.data || res || [];
    const creds = allCreds.slice(0, N);
    const tFetch1 = performance.now();

    const tWarm0 = performance.now();
    await vaultCrypt.warmDecryptCache(creds, masterKey);
    const tWarm1 = performance.now();

    const tMerkle0 = performance.now();
    await computeMerkleRoot(creds);
    const tMerkle1 = performance.now();

    const t1 = performance.now();

    log({
      op: 'login-e2e',
      N,
      totalMs: t1 - t0,
      fetchMs: tFetch1 - tFetch0,
      warmDecryptMs: tWarm1 - tWarm0,
      merkleMs: tMerkle1 - tMerkle0,
    });
  };

  const download = () => {
    const blob = new Blob([JSON.stringify(logs, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'securevault-bench-client.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const runAll = async () => {
    if (!masterKey) {
      alert('Unlock vault first so master key is available.');
      return;
    }
    setRunning(true);
    setLogs([]);

    try {
      logEnv();
      // PBKDF2 iteration set for primary benchmark (100k / 310k / 600k)
      const iterationSets = [100000, 310000, 600000];
      const argon2Configs = [
        { time: 2, mem: 32 * 1024, parallelism: 1 },
        { time: 3, mem: 64 * 1024, parallelism: 1 },
      ];

      // Warm-ups
      for (let i = 0; i < 3; i += 1) {
        // eslint-disable-next-line no-await-in-loop
        await runPBKDF2(310000, true);
        // eslint-disable-next-line no-await-in-loop
        await runEncryptDecrypt(true);
        // eslint-disable-next-line no-await-in-loop
        await runMerkle(100, true);
        // eslint-disable-next-line no-await-in-loop
        await runIncrementalMerkle(100, true);
      }

      // Merkle scaling up to 5000 credentials (as reported in paper)
      const Ns = [10, 50, 100, 250, 500, 1000, 2500, 5000];
      for (const N of Ns) {
        // For each vault size, sweep PBKDF2 iterations
        for (const iters of iterationSets) {
          // eslint-disable-next-line no-await-in-loop
          for (let i = 0; i < 10; i += 1) {
            // eslint-disable-next-line no-await-in-loop
            await runPBKDF2(iters, false);
            if (includeArgon2) {
              // eslint-disable-next-line no-await-in-loop
              for (const cfg of argon2Configs) {
                // eslint-disable-next-line no-await-in-loop
                await runArgon2id(cfg);
              }
            }
            // eslint-disable-next-line no-await-in-loop
            await runEncryptDecrypt(false);
            // eslint-disable-next-line no-await-in-loop
            await runMerkle(N, false);
            // eslint-disable-next-line no-await-in-loop
            await runIncrementalMerkle(N, false);
          }
        }
      }

      // End-to-end login-style benchmark for representative sizes
      const loginSizes = [100, 1000, 5000];
      for (const N of loginSizes) {
        // eslint-disable-next-line no-await-in-loop
        await runLoginE2E(N);
      }
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert(`Benchmark error: ${e.message || e}`);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <h2 className="text-xl font-semibold mb-2">SecureVault Benchmark Runner</h2>
      <p className="text-sm text-gray-600 mb-4">
        This tool collects client-side performance data (PBKDF2, Merkle computation, fetch+Merkle) for different vault sizes.
        Run it only in development on a stable network. After it finishes, download the JSON and analyze it with a script.
      </p>
      <div className="flex items-center gap-3 mb-4">
        <button
          type="button"
          onClick={runAll}
          disabled={running}
          className="px-4 py-2 rounded bg-blue-600 text-white disabled:bg-gray-400"
        >
          {running ? 'Running benchmarks…' : 'Run Benchmarks'}
        </button>
        <button
          type="button"
          onClick={download}
          disabled={logs.length === 0}
          className="px-4 py-2 rounded bg-gray-700 text-white disabled:bg-gray-400"
        >
          Download Logs
        </button>
        <label className="flex items-center gap-2 text-xs text-gray-700">
          <input
            type="checkbox"
            checked={includeArgon2}
            onChange={(e) => setIncludeArgon2(e.target.checked)}
          />
          Include Argon2id KDF benchmarks (slower)
        </label>
      </div>
      <div className="text-xs text-gray-700 border rounded p-2 bg-gray-50 max-h-64 overflow-auto">
        <pre className="whitespace-pre-wrap">
          {JSON.stringify(logs.slice(-10), null, 2)}
        </pre>
      </div>
    </div>
  );
};

export default BenchmarkRunner;


